import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../../utils/api";

const QuickPanelContext = createContext(null);

export const POSICOES = [
  ["top-left", "Superior esquerdo"],
  ["top-center", "Superior central"],
  ["top-right", "Superior direito"],
  ["bottom-left", "Inferior esquerdo"],
  ["bottom-center", "Inferior central"],
  ["bottom-right", "Inferior direito"],
];

export function useQuickPanel() {
  return useContext(QuickPanelContext) || {};
}

/**
 * Estado do apoio rápido: preferência do usuário (backend), qual drawer está
 * aberto e o deslocamento da sidebar. Um único drawer global por vez.
 */
export function QuickPanelProvider({ sidebarCollapsed = false, children }) {
  const [prefs, setPrefs] = useState({ enabled: true, position: "bottom-center" });
  const [carregado, setCarregado] = useState(false);
  const [aberto, setAberto] = useState(null); // "tasks" | "notes" | null
  const gatilhoRef = useRef(null);

  useEffect(() => {
    api
      .get("/me/quick-panel")
      .then(({ data }) => setPrefs(data))
      .catch(() => {})
      .finally(() => setCarregado(true));
  }, []);

  const salvarPrefs = useCallback(async (novas) => {
    const anterior = prefs;
    setPrefs(novas);
    try {
      await api.put("/me/quick-panel", novas);
    } catch (e) {
      setPrefs(anterior);
      throw e;
    }
  }, [prefs]);

  const abrir = useCallback((qual, gatilho) => {
    gatilhoRef.current = gatilho || null;
    setAberto(qual);
  }, []);

  // Notificação de tarefa abre direto o painel (link /dashboard?quick=tasks)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("quick") === "tasks") {
      setAberto("tasks");
      params.delete("quick");
      const busca = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${busca ? `?${busca}` : ""}`,
      );
    }
  }, []);

  const fechar = useCallback(() => {
    setAberto(null);
    // devolve o foco para quem abriu, como manda a acessibilidade
    setTimeout(() => gatilhoRef.current?.focus?.(), 0);
  }, []);

  const valor = useMemo(
    () => ({ prefs, carregado, salvarPrefs, aberto, abrir, fechar, sidebarCollapsed }),
    [prefs, carregado, salvarPrefs, aberto, abrir, fechar, sidebarCollapsed],
  );

  return <QuickPanelContext.Provider value={valor}>{children}</QuickPanelContext.Provider>;
}
